import React from 'react';
import { createRoot } from 'react-dom/client';
import { ServeCarousel } from '../src/components/ServeCarousel';
import type { ServeCarouselProps } from '../src/components/ServeCarousel';
import styleText from '../src/app/globals.css?inline';
import { attr, parseDataParams } from './params';

interface WidgetConfig extends ServeCarouselProps {
  containerId?: string;
}

declare global {
  interface Window {
    SERVE_CAROUSEL_CONFIG?: WidgetConfig;
    ServeCarouselWidget?: ServeCarouselWidgetInstance;
  }
}

class ServeCarouselWidgetInstance {
  private root: ReturnType<typeof createRoot> | null = null;
  private config: WidgetConfig;

  constructor(config: WidgetConfig = {}) {
    this.config = {
      containerId: 'serve-carousel-root',
      ...config,
    };
  }

  init() {
    const container = document.getElementById(this.config.containerId!);
    if (!container) {
      console.error(`Serve Carousel: Container #${this.config.containerId} not found`);
      return;
    }

    const shadowRoot = container.shadowRoot ?? container.attachShadow({ mode: 'open' });

    const mountPoint = document.createElement('div');
    mountPoint.id = 'serve-carousel-app';
    shadowRoot.appendChild(mountPoint);

    const fontId = 'serve-carousel-montserrat';
    if (!document.getElementById(fontId)) {
      const link = document.createElement('link');
      link.id = fontId;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap';
      document.head.appendChild(link);
    }

    const resetCSS = `
      *, *::before, *::after { box-sizing: border-box; }
      :host { display: block; width: 100%; }
      #serve-carousel-app { width: 100%; }
    `;
    let shadowCSS = styleText
      .replace(/:root\b/g, ':host')
      .replace(/\bbody\b/g, '#serve-carousel-app');
    const style = document.createElement('style');
    style.textContent = resetCSS + '\n' + shadowCSS;
    shadowRoot.appendChild(style);

    // Precedence: explicit config object > dedicated data-* attribute > the
    // shared data-params string.
    const fromParams = parseDataParams(container.getAttribute('data-params'));

    const campus = this.config.campus ?? attr(container.dataset.campus) ?? fromParams.campus;
    const ministryId =
      this.config.ministryId ??
      (attr(container.dataset.ministryId)
        ? Number(container.dataset.ministryId)
        : fromParams.ministryId);
    const programId =
      this.config.programId ??
      (attr(container.dataset.programId)
        ? Number(container.dataset.programId)
        : fromParams.programId);
    const search = this.config.search ?? attr(container.dataset.search) ?? fromParams.search;
    const viewAllUrl = this.config.viewAllUrl ?? attr(container.dataset.viewAllUrl);

    this.root = createRoot(mountPoint);
    this.root.render(
      <React.StrictMode>
        <div style={{ padding: '2rem' }}>
          <ServeCarousel
            apiBaseUrl={this.config.apiBaseUrl}
            campus={campus}
            ministryId={ministryId}
            programId={programId}
            search={search}
            viewAllUrl={viewAllUrl}
          />
        </div>
      </React.StrictMode>
    );
  }

  destroy() {
    this.root?.unmount();
    this.root = null;
  }
}

if (typeof window !== 'undefined') {
  // API base is the centralized api.woodsidebible.org host, baked at build
  // time from CHURCH_HUB_API_URL. (Do NOT derive it from the script origin —
  // the script is served from widgets.woodsidebible.org, which has no /api
  // routes, so that 404s and the widget silently falls back to sample data.)
  // window.SERVE_CAROUSEL_CONFIG can still override for local testing.
  const scriptOrigin = (() => {
    try {
      const src = document.currentScript?.getAttribute('src');
      if (src) return new URL(src, window.location.href).origin;
    } catch {}
    return window.location.origin;
  })();

  const config: WidgetConfig = {
    apiBaseUrl: process.env.CHURCH_HUB_API_URL || scriptOrigin,
    containerId: 'serve-carousel-root',
    ...(window.SERVE_CAROUSEL_CONFIG || {}),
  };

  if (!window.ServeCarouselWidget) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        const widget = new ServeCarouselWidgetInstance(config);
        widget.init();
        window.ServeCarouselWidget = widget;
      });
    } else {
      const widget = new ServeCarouselWidgetInstance(config);
      widget.init();
      window.ServeCarouselWidget = widget;
    }
  }
}

export default ServeCarouselWidgetInstance;
